import React, { useState, useEffect } from 'react';
import { BASE_URL } from './Config';
import config from "./common/Config";
import { toast } from 'sonner';
import CommonDatePicker from './datepicker/CommonDatePicker';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TableContainer,
  TextField,
  InputAdornment,
  TablePagination,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Skeleton,
  Link as MuiLink,
  Chip,
} from '@mui/material';
import { Iconify } from './Minimal/src/components/iconify/iconify';
import "./common/commonStyles.css";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../utils/companySettingsCache";
import { encryptPayload, normalizeEncryptedApiResponse } from "../utils/customerCrypto";

const columns = [
  { id: "sr_no", label: "Sr.No.", width: 60 },
  { id: "date", label: "Date", width: 120 },
  { id: "trip_number", label: "Trip ID", width: 120 },
  { id: "driver", label: "Driver", width: 150 },
  { id: "vehicle", label: "Vehicle", width: 150 },
  { id: "route_number", label: "Route", width: 150 },
  { id: "status", label: "Status", width: 120 },
];

const CompleteTrips = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem("token");

  const fetchData = async () => {
    if (!accessToken || !token) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const headers = { authorization: `Bearer ${token}` };
      let body;
      if (useEncrypt) {
        const plain = {
          searchByFromdate: fromDate,
          searchByTodate: toDate,
          draw: String(page + 1),
          start: String(page * rowsPerPage),
          length: String(rowsPerPage),
          search: { value: searchTerm },
        };
        const ep = encryptPayload(plain);
        if (!ep) {
          setData([]);
          setTotalRecords(0);
          return;
        }
        const fd = new FormData();
        fd.append("encrypted_payload", ep);
        body = fd;
      } else {
        const formData = new FormData();
        formData.append('searchByFromdate', fromDate);
        formData.append('searchByTodate', toDate);
        formData.append('draw', page + 1);
        formData.append('start', page * rowsPerPage);
        formData.append('length', rowsPerPage);
        formData.append('search[value]', searchTerm);
        body = formData;
      }

      const response = await fetch(`${config.API_URL}/get_complete_trip_list`, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const rawJson = await response.json();
      const json = normalizeEncryptedApiResponse(rawJson, useEncrypt) ?? rawJson;
      const trips = Array.isArray(json.data) ? json.data : [];

      const tripsWithSrNo = trips.map((trip, index) => ({
        ...trip,
        sr_no: page * rowsPerPage + index + 1
      }));

      setData(tripsWithSrNo);
      setTotalRecords(json.recordsTotal || json.recordsFiltered || trips.length);
    } catch {
      setData([]);
      setTotalRecords(0);
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchData();
  }, [fromDate, toDate, page, rowsPerPage, searchTerm, accessToken]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setSearchTerm(value);
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setPage(0);
    }
  };

  return (
    <>
        <Box sx={{ px:2,pb:1}}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box>
                <Typography className="page-title">
                  Closed Trips
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                className="input-border"
                fullWidth
                size="small"
                placeholder="Search trips..."
                value={searchInputValue}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-outline"/>
                      </InputAdornment>
                    )
                  }
                }}
            
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <CommonDatePicker
                    className="input-border"
                    value={fromDate && toDate ? [new Date(fromDate), new Date(toDate)] : [null, null]}
                    onChange={val => {
                      if (val && Array.isArray(val) && val[0] && val[1]) {
                        const startDate = val[0];
                        const endDate = val[1];
                        const formatDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        setFromDate(formatDate(startDate));
                        setToDate(formatDate(endDate));
                      } else {
                        setFromDate('');
                        setToDate('');
                      }
                    }}
                    showOneCalendar
                    format="yyyy-MM-dd"
                    character=" - "
                  />
                </Box>
            </Grid>
          </Grid>
        </Box>
        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table
            size="medium"
            className="table-cell-border"
          >
            <TableHead>
              <TableRow className="table-cell-header">
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.id === "sr_no" || column.id === "status" || column.id === "vehicle" ? "center" : "left"}
                    sx={{
                    
                      width: column.width,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {columns.map((column) => (
                      <TableCell
                        key={`${column.id}-${index}`}
                        align={column.id === "sr_no" || column.id === "status" || column.id === "vehicle" ? "center" : "left"}
                   
                      >
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
                : data.length > 0
                  ? data.map((row) => (
                    <TableRow
                      key={row.id || row.trip_number}
                      hover
                      className="table-cell-style"
                    >
                      <TableCell align="center" >
                        {row.sr_no}
                      </TableCell>
                      <TableCell >
                        {row.date}
                      </TableCell>
                      <TableCell >
                        <MuiLink
                          href={`${BASE_URL}/complete_trips/closed_trip_summary/${row.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: "inherit",
                            textDecoration: "underline !important",
                            textDecorationColor: "currentColor",
                            textUnderlineOffset: "2px",
                            "&:hover": {
                              textDecoration: "underline !important",
                              textDecorationColor: "currentColor"
                            }
                          }}
                        >
                          {row.trip_number}
                        </MuiLink>
                      </TableCell>
                      <TableCell >
                        {row.driver}
                      </TableCell>
                      <TableCell align="center" >
                        {row.vehicle}
                      </TableCell>
                      <TableCell >
                        {row.route_number}
                      </TableCell>
                      <TableCell align="center" >
                        <Chip
                          label={row.status}
                          size="small"
                         
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24,
                            backgroundColor: "#FFD399",
                            color: "#000",
                            "&:hover": {
                              backgroundColor: "#FFD399",
                              color: "#000",
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                  : (
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ py: 4 }}>
                        <Typography align="center" sx={{ color: "#637381", fontSize: "0.75rem" }}>
                          No records found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
            </TableBody>
          </Table>
        </TableContainer>
        {data.length > 0 && (
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="pagination-styles"
            />
          </Box>
        )}
    </>
  );
};

export default CompleteTrips;
